import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Coins, Crown, Zap, Check, Clock, Loader2, Sparkles, Info, RotateCcw, Settings, FileText, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api, { formatApiErrorDetail } from "@/lib/api";

const planIcons = { free: Sparkles, premium: Crown, pro: Zap };

export default function Credits() {
  const [balance, setBalance] = useState(null);
  const [plans, setPlans] = useState(null);
  const [history, setHistory] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [billing, setBilling] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [buying, setBuying] = useState(null);

  const loadBalance = () => api.get("/credits/balance").then((r) => setBalance(r.data)).catch(() => {});

  useEffect(() => {
    loadBalance();
    api.get("/credits/plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/analyses/history").then((r) => setHistory(r.data)).catch(() => {});
    api.get("/payments/history").then((r) => setPurchases(r.data)).catch(() => {});
    api.get("/billing").then((r) => setBilling(r.data)).catch(() => {});
  }, []);

  const startCheckout = async (lookupKey) => {
    setBuying(lookupKey);
    try {
      const r = await api.post("/payments/checkout", { lookup_key: lookupKey, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBuying(null);
    }
  };

  const restore = async () => {
    setRestoring(true);
    try {
      const r = await api.post("/billing/sync");
      setBilling(r.data);
      await loadBalance();
      toast.success("Acquisti ripristinati e stato aggiornato");
    } catch {
      toast.error("Impossibile ripristinare gli acquisti");
    } finally {
      setRestoring(false);
    }
  };

  const manage = async () => {
    try {
      const r = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = r.data.portal_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!balance || !plans)
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <Loader2 className="h-8 w-8 text-[#2f74f0] animate-spin" />
      </div>
    );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
      <span className="text-xs font-mono uppercase tracking-widest text-[#a9772f]">Abbonamenti & crediti</span>
      <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-[#2e2015] mt-2">Saldo e piani</h1>

      {/* Balance card */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-8 rounded-xl border border-[#1e5fe0]/25 bg-gradient-to-br from-white/80 to-[#c4d6e4] p-7 flex flex-wrap items-center justify-between gap-6"
      >
        <div className="flex items-center gap-4">
          <span className="grid place-items-center h-14 w-14 rounded-lg bg-[#1e5fe0]/10 border border-[#1e5fe0]/30">
            <Coins className="h-7 w-7 text-[#2f74f0]" />
          </span>
          <div>
            <div className="text-xs text-[#8a7760]">Crediti disponibili</div>
            <div data-testid="credits-balance" className="font-serif text-3xl text-[#2f74f0]">
              {balance.credits_remaining} <span className="text-lg text-[#6f5c47]">crediti</span>
            </div>
            <div className="text-sm text-[#8a7760] font-mono">
              = {balance.analyses_remaining} analisi outfit ({balance.analyses_per_credit} per credito)
            </div>
          </div>
        </div>
        <Badge className="bg-[#1e5fe0]/15 text-[#1e5fe0] border-[#1e5fe0]/30 capitalize text-sm px-4 py-1.5">
          Piano {balance.plan}
        </Badge>
      </motion.div>

      {/* Account actions */}
      <div className="mt-4 flex flex-wrap gap-3">
        <Button
          data-testid="restore-purchases-btn"
          onClick={restore}
          disabled={restoring}
          variant="outline"
          className="border-[#1e5fe0]/30 text-[#1a4fc0] hover:bg-[#1e5fe0]/10 hover:text-[#3a2a1c] bg-transparent"
        >
          {restoring ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
          Ripristina acquisti
        </Button>
        <Button
          data-testid="manage-subscription-btn"
          onClick={manage}
          variant="outline"
          className="border-[#1e5fe0]/30 text-[#1a4fc0] hover:bg-[#1e5fe0]/10 hover:text-[#3a2a1c] bg-transparent"
        >
          <Settings className="mr-2 h-4 w-4" /> Gestisci abbonamento
        </Button>
      </div>

      {/* Plans */}
      <h2 className="font-serif text-2xl text-[#2e2015] mt-12 mb-5">Piani di abbonamento</h2>
      <div className="grid md:grid-cols-3 gap-6">
        {plans.plans.map((p) => {
          const Icon = planIcons[p.id] || Sparkles;
          const isPro = p.id === "pro";
          return (
            <div
              key={p.id}
              data-testid={`plan-card-${p.id}`}
              className={`rounded-xl border p-6 ${isPro ? "border-[#2f74f0]/50 bg-[#1e5fe0]/5 gold-glow" : "border-[#1e5fe0]/20 bg-white/75"}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <Icon className="h-5 w-5 text-[#2f74f0]" />
                <h3 className="font-serif text-xl text-[#2e2015]">{p.name}</h3>
                {isPro && <Badge className="bg-[#1e5fe0] text-white ml-auto">Top</Badge>}
              </div>
              <div className="font-mono text-2xl text-[#2f74f0] mb-1">{p.monthly_credits} crediti/mese</div>
              <div className="text-sm text-[#8a7760] mb-2">{p.monthly_analyses} analisi outfit al mese</div>
              {p.price_eur && (
                <div className="font-mono text-xl text-[#2e2015] mb-4">
                  € {p.price_eur.toFixed(2)}<span className="text-sm text-[#8a7760]">/mese</span>
                </div>
              )}
              <ul className="space-y-2 text-sm text-[#4d3a2a] mb-5">
                <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-400 shrink-0" /> Analisi corporatura illimitata</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-400 shrink-0" /> Generazione outfit con IA</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-400 shrink-0" /> Guardaroba e lista acquisti</li>
              </ul>
              <Button
                data-testid={`plan-select-${p.id}`}
                disabled={p.type === "trial" || buying === p.rc_product_id}
                onClick={() => p.rc_product_id && startCheckout(p.rc_product_id)}
                className={`w-full ${isPro ? "bg-[#1e5fe0] hover:bg-[#2f74f0] text-white" : "bg-[#cfdde9] hover:bg-[#bcd0e0] text-[#2e2015]"} font-semibold`}
              >
                {p.type === "trial"
                  ? "Piano attuale"
                  : buying === p.rc_product_id
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : "Abbonati ora"}
              </Button>
            </div>
          );
        })}
      </div>

      {/* Credit packs — sezione acquisto crediti (prominente) */}
      <div className="mt-14 rounded-2xl border-2 border-[#1e5fe0]/30 bg-white/80 p-6 sm:p-8 gold-glow">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-1">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2e2015]">Acquista crediti per i tuoi outfit</h2>
          <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#a9772f]">
            <Coins className="h-4 w-4" /> 1 credito = €2,50 = 3 outfit
          </span>
        </div>
        <p className="text-[#6f5c47] mb-6">
          Ogni credito genera 3 outfit completi. Più crediti acquisti, più outfit puoi creare: il listino scala all'aumentare del pacchetto.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {plans.credit_packs.map((pack, i) => (
            <div
              key={pack.rc_product_id}
              data-testid={`pack-card-${pack.rc_product_id}`}
              className={`rounded-xl border p-5 text-center bg-white ${i === plans.credit_packs.length - 1 ? "border-[#1e5fe0]/50" : "border-[#1e5fe0]/20"}`}
            >
              <Coins className="h-7 w-7 text-[#2f74f0] mx-auto mb-2" />
              <div className="font-serif text-2xl text-[#2e2015]">{pack.credits}</div>
              <div className="text-xs text-[#8a7760] mb-1">{pack.credits === 1 ? "credito" : "crediti"}</div>
              <div className="text-sm font-medium text-[#4d3a2a] mb-3">{pack.analyses} outfit</div>
              <div className="font-mono text-[#1e5fe0] text-lg mb-3">€ {pack.price_eur.toFixed(2)}</div>
              <Button data-testid={`pack-buy-${pack.rc_product_id}`} onClick={() => startCheckout(pack.rc_product_id)} disabled={buying === pack.rc_product_id} className="w-full bg-[#1e5fe0] hover:bg-[#2f74f0] text-white font-semibold h-9">
                {buying === pack.rc_product_id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Acquista"}
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-[#1e5fe0]/20 bg-[#1e5fe0]/5 p-4 flex items-start gap-3">
        <Info className="h-5 w-5 text-[#2f74f0] shrink-0 mt-0.5" />
        <p className="text-sm text-[#6f5c47]">
          Pagamento sicuro con <strong className="text-[#3a2a1c]">carta di credito</strong>, Apple Pay o Google Pay tramite{" "}
          <strong className="text-[#3a2a1c]">Stripe</strong>. I crediti vengono accreditati subito dopo il pagamento.
        </p>
      </div>

      {/* Purchase history */}
      <h2 className="font-serif text-2xl text-[#2e2015] mt-12 mb-5">Storico acquisti</h2>
      {purchases.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[#1e5fe0]/25 bg-white/70 p-10 text-center text-[#8a7760]">
          <FileText className="h-8 w-8 text-[#a9772f]/50 mx-auto mb-3" />
          Nessun acquisto ancora effettuato.
        </div>
      ) : (
        <div data-testid="purchase-history" className="rounded-xl border border-[#1e5fe0]/15 bg-white/75 divide-y divide-[#1e5fe0]/10">
          {purchases.map((p) => (
            <div key={p.session_id} data-testid={`purchase-row-${p.session_id}`} className="flex items-center justify-between px-5 py-3.5 text-sm gap-4">
              <div className="min-w-0">
                <div className="text-[#3a2a1c] font-medium">
                  {p.kind} · <span className="text-[#1a4fc0]">{p.label}</span>
                </div>
                <div className="text-xs text-[#8a7760] font-mono">
                  {p.created_at ? new Date(p.created_at).toLocaleString("it-IT") : ""}
                </div>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <span className="font-mono text-[#2f74f0]">
                  {(p.amount / 100).toFixed(2)} {p.currency === "EUR" ? "€" : p.currency}
                </span>
                {p.receipt_url ? (
                  <a
                    data-testid={`receipt-link-${p.session_id}`}
                    href={p.receipt_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-[#6f5c47] hover:text-[#1e5fe0]"
                  >
                    Ricevuta <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <span className="text-xs text-[#b8a894]">—</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* History */}
      <h2 className="font-serif text-2xl text-[#2e2015] mt-12 mb-5">Storico delle analisi</h2>
      {history.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[#1e5fe0]/25 bg-white/70 p-10 text-center text-[#8a7760]">
          <Clock className="h-8 w-8 text-[#a9772f]/50 mx-auto mb-3" />
          Nessuna analisi ancora effettuata.
        </div>
      ) : (
        <div data-testid="analyses-history" className="rounded-xl border border-[#1e5fe0]/15 bg-white/75 divide-y divide-[#1e5fe0]/10">
          {history.map((h) => (
            <div key={h.id} className="flex items-center justify-between px-5 py-3.5 text-sm">
              <div>
                <div className="text-[#3a2a1c]">{h.title || "Analisi outfit"}</div>
                <div className="text-xs text-[#8a7760] font-mono">
                  {h.style ? `${h.style} · ` : ""}{new Date(h.created_at).toLocaleString("it-IT")}
                </div>
              </div>
              <Badge variant="outline" className="border-[#1e5fe0]/30 text-[#1a4fc0] font-mono">-1 analisi</Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
